import * as Sentry from '@sentry/browser';
import {memoize} from 'lodash-es';
import type {SentryTeamName} from '@stripe-internal/connect-embedded-lib';
import {getPlatformURL} from '@stripe-internal/connect-embedded-lib';
import type {PrimitiveRecord} from '../../utils/errorHandling';
import {getDSNForComponent} from '../../utils/getTeamForComponent';
import {getDevLogger} from '../../utils/getLogger';
import type {ServiceEnvironment} from '../../utils/getTargetEnvironment';
import {isDevEnvironment} from '../../utils/isDevEnvironment';
import {isAnalyticsDisabled} from '../isAnalyticsDisabled';
import {getPageViewId} from '../getPageViewId';

// Known-noisy messages mirrored from CAX embedded Sentry grouping rules.
const FILTERED_MESSAGE_SUBSTRINGS = [
  'Native fetch error',
  'network_error',
  'Failed to claim account session',
  'Stripe.js integrations must use HTTPS',
  'Failed to load Stripe.js',
  'font-property.stretch contains invalid characters',
  'Not implemented on this platform',
  'Expired API Key provided',
];

/**
 * Returns true if the Sentry event matches one of the known-noisy issue
 * groupings borrowed from the CAX embedded Sentry project.  Events that match
 * should be dropped (beforeSend should return null).
 */
export const shouldFilterSentryEvent = (event: Sentry.Event): boolean => {
  const message = event.message ?? event.exception?.values?.[0]?.value ?? '';
  const errorType = event.exception?.values?.[0]?.type ?? '';

  if (FILTERED_MESSAGE_SUBSTRINGS.some((s) => message.includes(s))) {
    return true;
  }

  if (message === 'Failed to fetch' && errorType === 'TypeError') {
    return true;
  }

  const frames =
    event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
  return frames.some((frame) => frame.abs_path?.includes('chrome-extension'));
};

export const configureScopeCallback = (
  scope: Sentry.Scope,
  extraFingerprints: string[],
  releaseCandidate?: string,
) => {
  // Set the page view id and current page url as an extra parameter
  scope.setTag('page_view_id', getPageViewId() || '');
  scope.setTag('platform_url', getPlatformURL() || '');

  if (releaseCandidate && /^\w+$/.test(releaseCandidate)) {
    scope.setTag('is_rc', true);
    scope.setTag('rc_commit_hash', releaseCandidate);
  }

  // Grouping events by specifying their fingerprint value
  // See https://docs.sentry.io/platforms/node/usage/sdk-fingerprinting/
  scope.setFingerprint(['{{ default }}', ...extraFingerprints]);

  return scope;
};

export interface IErrorPayload {
  error: any; // Using any here to mimic sentry API
  connectElement: string | undefined;
  invokingComponent: string | undefined;
  tags?: PrimitiveRecord;
  extra?: PrimitiveRecord;
  owner?: SentryTeamName;
  level?: Sentry.SeverityLevel;
}

export interface IErrorReporter {
  captureException: (errorPayload: IErrorPayload) => void;
}

const devLogger = getDevLogger();

/**
 * This class simply wraps sentry methods
 */
export class ErrorReporter implements IErrorReporter {
  private defaultOptions: Sentry.BrowserOptions;

  constructor(
    private serviceEnvironment: ServiceEnvironment,
    private releaseCandidate: string | undefined,
    private analyticsDisabledFromFrameInit: boolean | undefined,
  ) {
    this.defaultOptions = {
      integrations: [new Sentry.Integrations.UserAgent()],
      defaultIntegrations: false,
      environment: isDevEnvironment ? 'localdev' : 'production',
      beforeSend: (event, _hint) => {
        // TODO Filter events based on Event source (only send connect elements events) - SUS-183
        if (shouldFilterSentryEvent(event)) {
          return null;
        }
        return event;
      },
    };
  }

  getSentryForComponent = memoize(
    (component?: string, owner?: SentryTeamName) => {
      const client = new Sentry.BrowserClient({
        ...this.defaultOptions,
        dsn: getDSNForComponent(component, owner)[
          this.serviceEnvironment === 'prod' && !isDevEnvironment
            ? 'prod'
            : 'qa'
        ],
      });
      return new Sentry.Hub(client);
    },
  );

  public captureException(errorPayload: IErrorPayload) {
    devLogger.error(
      'An error report will be sent with the following properties:',
      errorPayload,
    );

    const {error, connectElement, tags, extra, owner, level} = errorPayload;

    this.getSentryForComponent(connectElement, owner).run((currentHub) => {
      currentHub.configureScope((scope) => {
        const severity = () => {
          switch (level) {
            case 'warning':
              return Sentry.Severity.Warning;
            case 'info':
              return Sentry.Severity.Info;
            default:
              return Sentry.Severity.Error;
          }
        };

        scope.setTags(tags || {});
        scope.setExtras(extra || {});
        scope.setLevel(severity());
        const extraFingerprints: string[] = [
          connectElement,
          tags?.type,
          tags?.stripeErrorCode,
          tags?.stripeStatusCode,
        ].map((e) => e?.toString() || '');

        return configureScopeCallback(
          scope,
          extraFingerprints,
          this.releaseCandidate,
        );
      });

      if (!isAnalyticsDisabled(this.analyticsDisabledFromFrameInit)) {
        currentHub.captureException(error);
      }
    });
  }
}
